import type { User } from "@/Slices/authenticationSlice";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import type { User as OpenAPIUser } from "@freightsimple/generated-dashboard-openapi-client/models/User";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import {
  Menu,
  MenuGroup,
  MenuSeparator,
} from "@freightsimple/ui-core/Menu/Menu";
import { T } from "@freightsimple/ui-core/Typography/T";
import { UserSelectType } from "@/Features/Dashboard/Exports/Hooks/useDashboardUserSelect";
import { BranchFilterOptionType } from "@/Shared/Types/BranchFilterOptionType";
import { DashboardUserSelectItem } from "./DashboardUserSelectItem";

interface UserSelectProps {
  type: UserSelectType;
  user: User;
  users: OpenAPIUser[];
  branches: SavedLocation[];
  selectedUserId: string | undefined;
  selectedBranchId: string | undefined | BranchFilterOptionType;
  onSelectUser: (selectedUserId: string | undefined) => void;
  onSelectBranch: (
    selectedBranchId: string | undefined | BranchFilterOptionType,
  ) => void;
  showUncodedOption: boolean;
}

export function DashboardUserSelect(props: UserSelectProps) {
  function title() {
    switch (props.type) {
      case UserSelectType.Booking:
        return "Booked By";
      case UserSelectType.Quoting:
        return "Quoted By";
    }
  }

  const users = props.users;
  const branches = props.branches;
  const selectedUserId = props.selectedUserId;
  const filteredUsers = props.users.filter(function (user) {
    return user.userId !== props.user.userId;
  });

  const selectedUser = users.find(function (user) {
    return user.userId === selectedUserId;
  });

  const selectedBranch = branches.find(function (branch) {
    return branch.savedLocationId === props.selectedBranchId;
  });

  function getSelectedName() {
    if (selectedUser) {
      return selectedUser.name;
    }

    if (selectedBranch) {
      return selectedBranch.location.businessName;
    }

    if (props.selectedBranchId === BranchFilterOptionType.Uncoded) {
      return "Not Coded to Branch";
    }

    return `Any User / Branch`;
  }

  const selectedUserName = getSelectedName();
  const nothingSelected =
    props.selectedUserId === undefined && props.selectedBranchId === undefined;

  function selectUser(userId: string | undefined) {
    props.onSelectUser(userId);
    props.onSelectBranch(undefined);
  }

  function selectBranch(branchId: string | undefined | BranchFilterOptionType) {
    props.onSelectUser(undefined);
    props.onSelectBranch(branchId);
  }

  return (
    <Menu
      width="wide"
      // Every row is a person or a branch, so none of them leads with an icon.
      // Without this the labels sit 22px right of their own group headers.
      iconColumn={false}
      trigger={
        <button
          type="button"
          data-testid="user-select"
          className="flex cursor-pointer flex-col items-end"
        >
          <T size="xs" weight="normal" color="muted">
            {title()}
          </T>
          <span className="-mt-1 mb-2 text-[18px] font-bold text-foreground">
            {selectedUserName}
          </span>
        </button>
      }
    >
      <DashboardUserSelectItem
        selected={nothingSelected}
        onSelect={() => selectUser(undefined)}
      >
        Any User / Branch
      </DashboardUserSelectItem>
      <MenuSeparator />
      <MenuGroup label="You">
        <DashboardUserSelectItem
          selected={props.selectedUserId === props.user.userId}
          onSelect={() => selectUser(props.user.userId)}
        >
          {props.user.name}
        </DashboardUserSelectItem>
      </MenuGroup>
      {branches.length > 0 && (
        <>
          <MenuSeparator />
          <MenuGroup label="Branches">
            {branches.map(function (branch) {
              return (
                <DashboardUserSelectItem
                  key={branch.savedLocationId}
                  selected={props.selectedBranchId === branch.savedLocationId}
                  onSelect={() => selectBranch(branch.savedLocationId)}
                >
                  {branch.location.businessName}
                </DashboardUserSelectItem>
              );
            })}
            {props.showUncodedOption && (
              <DashboardUserSelectItem
                selected={
                  props.selectedBranchId === BranchFilterOptionType.Uncoded
                }
                onSelect={() => selectBranch(BranchFilterOptionType.Uncoded)}
              >
                Not Coded to Branch
              </DashboardUserSelectItem>
            )}
          </MenuGroup>
        </>
      )}
      <MenuSeparator />
      <MenuGroup label="Other Users">
        {filteredUsers.map(function (user) {
          return (
            <DashboardUserSelectItem
              key={user.userId}
              selected={props.selectedUserId === user.userId}
              onSelect={() => selectUser(user.userId)}
            >
              {user.name}
            </DashboardUserSelectItem>
          );
        })}
        {users.length === 0 && (
          <div className="mt-2 pr-6 pl-3 text-xs font-light text-content-muted">
            You can invite others to this company{" "}
            <Link href="/manage-users">here</Link>
          </div>
        )}
      </MenuGroup>
    </Menu>
  );
}
